<template>
  <main class="container-1 notifications-history">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
    </div>

    <section class="container-body">
      <div class="notifications-history__toolbar">
        <label class="notifications-history__filter">
          Mostrar
          <select v-model="scope" @change="onFilterChange">
            <option value="current">Vigentes</option>
            <option value="all">Todas</option>
          </select>
        </label>

        <label class="notifications-history__filter notifications-history__checkbox">
          <input type="checkbox" v-model="unreadOnly" @change="onFilterChange">
          Solo no leídas
        </label>

        <button
          v-if="unreadTotal > 0"
          type="button"
          class="notifications-history__mark-all"
          @click="markAllRead"
        >
          Marcar todas como leídas ({{ unreadTotal }})
        </button>
      </div>

      <p v-if="loading" class="notifications-history__state">Cargando notificaciones...</p>
      <p v-else-if="error" class="notifications-history__state notifications-history__error" role="alert">{{ error }}</p>
      <p v-else-if="!notifications.length" class="notifications-history__state">No hay notificaciones para este filtro.</p>

      <ul v-else class="notifications-history__list">
        <template v-for="n in notifications">
          <li :key="n.id">
            <router-link
              v-if="itemLink(n)"
              :to="itemLink(n)"
              class="notifications-history__item"
              :class="{ 'notifications-history__item--unread': !n.read }"
              @click.native="onItemClick(n)"
            >
              <strong>{{ n.title }}</strong>
              <p>{{ n.message }}</p>
              <small>{{ formatNotificationDate(n.created_at) }}<span v-if="n.expired"> · Expirada</span></small>
            </router-link>
            <div
              v-else
              class="notifications-history__item"
              :class="{ 'notifications-history__item--unread': !n.read }"
              role="button"
              tabindex="0"
              @click="onItemClick(n)"
              @keydown.enter="onItemClick(n)"
            >
              <strong>{{ n.title }}</strong>
              <p>{{ n.message }}</p>
              <small>{{ formatNotificationDate(n.created_at) }}<span v-if="n.expired"> · Expirada</span></small>
            </div>
          </li>
        </template>
      </ul>

      <Pagination
        v-show="totalPages !== 0"
        :totalPages="totalPages"
        :currentPage="currentPage"
        :pageSize="pageSize"
        @pagechanged="onPageChanged"
      />
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import Pagination from "@/components/Pagination.vue";
import { notificationsApi } from "@/services/notifications/api.js";
import { resolveNotificationLink } from "@/services/notifications/links.js";
import { formatNotificationDate } from "@/services/notifications/format.js";

export default {
  name: "NotificationsHistory",

  components: { Breadcrumb, Pagination },

  data() {
    return {
      notifications: [],
      unreadTotal: 0,
      scope: "current",
      unreadOnly: false,
      currentPage: 1,
      pageSize: 10,
      totalPages: 0,
      loading: false,
      error: "",
    };
  },

  computed: {
    routesBread() {
      return [{ name: "Notificaciones" }];
    },
    terrainPermissions() {
      const permissions = this.$store.getters.getPermissions;
      return (permissions && permissions.terrain) || null;
    },
  },

  created() {
    this.loadPage();
  },

  methods: {
    itemLink(notification) {
      return resolveNotificationLink(notification, this.terrainPermissions);
    },
    formatNotificationDate,

    onFilterChange() {
      this.currentPage = 1;
      this.loadPage();
    },
    onPageChanged(page) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.loadPage();
    },

    async loadPage() {
      this.loading = true;
      this.error = "";
      try {
        const data = await notificationsApi.getInbox({
          page: this.currentPage, limit: this.pageSize, scope: this.scope, unreadOnly: this.unreadOnly,
        });
        this.notifications = data.notifications || [];
        this.totalPages = (data.pagination && data.pagination.total_pages) || 0;
        this.unreadTotal = data.unread_total || 0;
      } catch (error) {
        console.error(error);
        this.notifications = [];
        this.totalPages = 0;
        this.error = "No fue posible cargar las notificaciones.";
      } finally {
        this.loading = false;
      }
    },

    async onItemClick(notification) {
      if (notification.read) return;
      const previousUnread = this.unreadTotal;
      notification.read = true;
      this.unreadTotal = Math.max(0, this.unreadTotal - 1);
      try {
        const data = await notificationsApi.markRead(notification.id);
        this.unreadTotal = data.unread;
      } catch (error) {
        notification.read = false;
        this.unreadTotal = previousUnread;
        this.$store.dispatch("toast/show", { message: "No se pudo marcar la notificación como leída.", type: "error" });
      }
    },
    async markAllRead() {
      const previousReadState = this.notifications.map((n) => n.read);
      const previousUnread = this.unreadTotal;
      this.notifications.forEach((n) => { n.read = true; });
      this.unreadTotal = 0;
      try {
        const data = await notificationsApi.markAllRead();
        this.unreadTotal = data.unread;
      } catch (error) {
        this.notifications.forEach((n, index) => { n.read = previousReadState[index]; });
        this.unreadTotal = previousUnread;
        this.$store.dispatch("toast/show", { message: "No se pudieron marcar las notificaciones como leídas.", type: "error" });
      }
    },
  },
};
</script>

<style scoped>
.notifications-history__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.2em;

  margin-bottom: 1em;
}

.notifications-history__filter {
  display: flex;
  align-items: center;
  gap: 0.5em;

  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-navy);
}

.notifications-history__checkbox input {
  width: auto;
}

.notifications-history__mark-all {
  margin-left: auto;

  padding: 0.5em 0.9em;
  border: 1px solid var(--color-orange);
  border-radius: 4px;

  background: #fff;
  color: var(--color-orange);

  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.notifications-history__mark-all:hover {
  background: var(--color-orange);
  color: #fff;
}

.notifications-history__state {
  padding: 1.5em 0;
  color: var(--color-ghost);
  text-align: center;
}

.notifications-history__error {
  color: var(--color-danger);
}

.notifications-history__list {
  display: flex;
  flex-direction: column;
  gap: 0.6em;

  list-style: none;
  margin: 0 0 1.2em;
  padding: 0;
}

.notifications-history__item {
  display: block;

  padding: 0.9em 1em;
  border: 1px solid var(--color-fog);
  border-left: 3px solid transparent;
  border-radius: 6px;

  background: #fff;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.notifications-history__item--unread {
  border-left-color: var(--color-orange);
  background: var(--color-mist);
}

.notifications-history__item strong {
  display: block;
  color: var(--color-navy);
  font-size: 0.9rem;
}

.notifications-history__item--unread strong {
  font-weight: 800;
}

.notifications-history__item p {
  margin: 0.35em 0;
  color: var(--color-graphite);
  font-size: 0.82rem;
  line-height: 1.4;
}

.notifications-history__item small {
  color: var(--color-ghost);
  font-size: 0.72rem;
}
</style>
